import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useSetTopBarSlots } from '@/components/TopBarSlots';
import {
  useDeleteNote,
  useNote,
  useUpdateNote,
  useWorkspace,
} from '@fluxter/shared';
import { ArrowUpRight, Check, Trash2 } from 'lucide-react';
import { CommentThread } from '@/components/CommentThread';
import { useConfirm } from '@/components/ConfirmDialog';
import { MemberAvatar } from '@/components/MemberAvatar';
import { UserName } from '@/components/UserName';
import { PanelCard, PanelInlineRow, PanelStackedRow } from '@/components/SidePanel';
import { SkeletonStack } from '@/components/Skeletons';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { formatRelativeTime } from '@/lib/relative-time';
import { cn } from '@/lib/utils';

/**
 * Lazy-load the editor + its TipTap/ProseMirror dependency tree.
 * `NoteEditor` is the *only* importer of `@tiptap/*` in the SPA, so
 * splitting it here puts ProseMirror, StarterKit, the Link extension,
 * and the Placeholder extension all in a single chunk that's only
 * fetched when a user actually opens a note detail page. Workspaces
 * that primarily use the board / time / calendar views never pay the
 * cost.
 */
const NoteEditor = lazy(() =>
  import('@/components/NoteEditor').then((m) => ({ default: m.NoteEditor })),
);

interface NoteAttrs {
  title?: string;
  body?: string;
  workspace_id?: string;
  project_id?: string | null;
  project_slug?: string | null;
  project_name?: string | null;
  task_id?: string | null;
  task_title?: string | null;
  created_by?: string | null;
  creator_name?: string | null;
  creator_email?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

interface NotePayload {
  data?: { id: string; attributes?: NoteAttrs };
}

type SavingState = 'idle' | 'saving' | 'saved' | 'error';

const formatExact = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

export const NoteDetailPage = () => {
  const { workspaceSlug, noteUlid } = useParams({ from: '/w/$workspaceSlug/n/$noteUlid' });
  const navigate = useNavigate();
  const workspace = useWorkspace(workspaceSlug);
  const note = useNote(workspaceSlug, noteUlid);
  const updateNote = useUpdateNote(workspaceSlug);
  const deleteNote = useDeleteNote(workspaceSlug);
  const { confirm, dialog } = useConfirm();

  const workspaceName = (workspace.data as { data?: { attributes?: { name?: string } } } | undefined)
    ?.data?.attributes?.name;
  const attrs = (note.data as NotePayload | undefined)?.data?.attributes;

  // Title is the only field with an explicit local-state-mirror — the
  // body lives inside TipTap and is read out on blur via NoteEditor's
  // onBlur callback. Keeping title in React state lets the breadcrumb
  // and `<title>` reflect changes immediately as the user types.
  const [title, setTitle] = useState('');
  const [savingState, setSavingState] = useState<SavingState>('idle');
  const [error, setError] = useState<string | null>(null);
  const titleRef = useRef<HTMLInputElement | null>(null);
  const didAutoFocusTitle = useRef(false);

  // Hydrate title when the note loads or changes from the server.
  useEffect(() => {
    if (!attrs) return;
    setTitle(attrs.title ?? '');
  }, [attrs]);

  // Fresh notes (those just minted by ProjectNotesPage's "+ New note"
  // button) carry the placeholder title "Untitled" and an empty body —
  // focus the title input on first paint so the user can immediately
  // retype it. Existing notes get no autofocus; the user can click the
  // body to start typing if they want.
  const isFreshNote =
    !!attrs && (attrs.title ?? '').trim().toLowerCase() === 'untitled' && (attrs.body ?? '') === '';

  useEffect(() => {
    if (isFreshNote && !didAutoFocusTitle.current && titleRef.current) {
      titleRef.current.focus();
      titleRef.current.select();
      didAutoFocusTitle.current = true;
    }
  }, [isFreshNote]);

  const flashSavedAfter = (delay = 1800): void => {
    setSavingState('saved');
    window.setTimeout(() => {
      setSavingState((prev) => (prev === 'saved' ? 'idle' : prev));
    }, delay);
  };

  const commitChange = async (changes: { title?: string; body?: string }): Promise<void> => {
    if (!attrs) return;
    // No-op if the field hasn't actually changed — avoids a save for
    // every blur that fires without user input.
    const titleUnchanged = changes.title === undefined || changes.title === (attrs.title ?? '');
    const bodyUnchanged = changes.body === undefined || changes.body === (attrs.body ?? '');
    if (titleUnchanged && bodyUnchanged) return;

    setError(null);
    setSavingState('saving');
    try {
      await updateNote.mutateAsync({ noteUlid, ...changes });
      flashSavedAfter();
    } catch (e) {
      setSavingState('error');
      setError(e instanceof Error ? e.message : 'Could not save note.');
    }
  };

  const handleTitleBlur = (): void => {
    const trimmed = title.trim();
    if (trimmed.length === 0) {
      // Reject the empty title locally — restore the previous value so
      // the input doesn't show the empty string.
      setTitle(attrs?.title ?? '');
      setError('Title cannot be empty.');
      return;
    }
    void commitChange({ title: trimmed });
  };

  const handleBodyBlur = (html: string): void => {
    void commitChange({ body: html });
  };

  const handleDelete = async (): Promise<void> => {
    if (!attrs) return;
    const ok = await confirm({
      title: `Delete "${attrs.title ?? 'this note'}"?`,
      description: "This can't be undone.",
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await deleteNote.mutateAsync(noteUlid);
      // Send the user back to wherever the note hung from.
      if (attrs.project_slug) {
        await navigate({
          to: '/w/$workspaceSlug/p/$projectSlug/notes',
          params: { workspaceSlug, projectSlug: attrs.project_slug },
        });
      } else if (attrs.task_id) {
        await navigate({
          to: '/w/$workspaceSlug/t/$taskUlid',
          params: { workspaceSlug, taskUlid: attrs.task_id },
        });
      } else {
        await navigate({ to: '/w/$workspaceSlug/notes', params: { workspaceSlug } });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not delete note.');
    }
  };

  /* ── Topbar contributions ───────────────────────────────────────
   *  Breadcrumb (left) and SaveIndicator (right) both move to the
   *  persistent topbar — same pattern as project pages. Memoized so
   *  the slot effect only refires when the displayed values change.
   *  Hooks must run unconditionally; the memos return null while the
   *  note is loading, which the topbar treats as "no contribution".
   */
  const topBarLeft = useMemo(() => {
    if (!attrs) return null;
    return (
      <nav
        aria-label="Breadcrumb"
        className="flex items-center gap-1.5 text-[13px]"
      >
        <Link
          to="/w/$workspaceSlug"
          params={{ workspaceSlug }}
          className="text-foreground-muted hover:text-foreground transition-colors"
        >
          {workspaceName ?? 'Workspace'}
        </Link>
        <span aria-hidden="true" className="text-foreground-faint">
          /
        </span>
        {attrs.project_slug ? (
          <>
            <Link
              to="/w/$workspaceSlug/p/$projectSlug/notes"
              params={{ workspaceSlug, projectSlug: attrs.project_slug }}
              className="text-foreground-muted hover:text-foreground transition-colors"
            >
              {attrs.project_name ?? 'Project'} · Notes
            </Link>
            <span aria-hidden="true" className="text-foreground-faint">
              /
            </span>
          </>
        ) : attrs.task_id ? (
          <>
            <Link
              to="/w/$workspaceSlug/t/$taskUlid"
              params={{ workspaceSlug, taskUlid: attrs.task_id }}
              className="text-foreground-muted hover:text-foreground transition-colors"
            >
              {attrs.task_title ?? 'Task'}
            </Link>
            <span aria-hidden="true" className="text-foreground-faint">
              /
            </span>
          </>
        ) : (
          <>
            <Link
              to="/w/$workspaceSlug/notes"
              params={{ workspaceSlug }}
              className="text-foreground-muted hover:text-foreground transition-colors"
            >
              Notes
            </Link>
            <span aria-hidden="true" className="text-foreground-faint">
              /
            </span>
          </>
        )}
        <span
          className="text-foreground max-w-[280px] truncate font-medium"
          aria-current="page"
        >
          {title || 'Untitled note'}
        </span>
      </nav>
    );
  }, [attrs, workspaceSlug, workspaceName, title]);

  const topBarRight = useMemo(
    () => <SaveIndicator state={savingState} />,
    [savingState],
  );

  useSetTopBarSlots({ left: topBarLeft, right: topBarRight });

  if (note.isLoading) {
    return (
      <>
        <div className="space-y-6">
          <Skeleton className="h-9 w-48" />
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
            <div className="min-w-0 space-y-4">
              <Skeleton className="h-9 w-3/4" />
              <SkeletonStack lines={8} />
            </div>
            <Skeleton className="hidden h-64 w-full lg:block" />
          </div>
        </div>
      </>
    );
  }

  if (!attrs) {
    return (
      <>
        <p className="text-foreground-faint text-[12.5px]">Note not found.</p>
      </>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Breadcrumb + SaveIndicator now live in the persistent topbar
            (left + right slots). The page just owns the editor canvas. */}
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
          {/* ── Main column — always-on writing surface ───────────────── */}
          <div className="min-w-0 space-y-4">
            <Input
              ref={titleRef}
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={handleTitleBlur}
              placeholder="Untitled note"
              aria-label="Note title"
              maxLength={255}
              className={cn(
                // Glass title scale — 28px / 600 / -0.025em — matches
                // the task-detail title and workspace-home greeting so
                // detail pages share a single anchor scale.
                'text-foreground h-auto border-none bg-transparent px-0 py-1 text-[28px] font-semibold tracking-[-0.025em] shadow-none focus-visible:ring-0',
              )}
            />

            {error ? (
              <p className="text-destructive text-[12.5px]" role="alert">
                {error}
              </p>
            ) : null}

            <Suspense fallback={<EditorSkeleton />}>
              <NoteEditor
                key={noteUlid}
                content={attrs.body ?? ''}
                onBlur={handleBodyBlur}
                workspaceSlug={workspaceSlug}
              />
            </Suspense>

            <CommentThread
              workspaceSlug={workspaceSlug}
              parent={{ type: 'note', noteUlid }}
            />
          </div>

          {/* ── Side panel ───────────────────────────────────────────── */}
          <aside className="space-y-4 lg:sticky lg:top-2 lg:self-start">
            <PanelCard title="Actions">
              <PanelStackedRow label="Danger">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => void handleDelete()}
                  disabled={deleteNote.isPending}
                  className="text-muted-foreground hover:text-destructive w-full justify-start"
                >
                  <Trash2 className="mr-1.5 size-4" aria-hidden="true" />
                  Delete note
                </Button>
              </PanelStackedRow>
            </PanelCard>

            <PanelCard title="Activity">
              <PanelInlineRow
                label="Author"
                value={
                  <span className="flex items-center gap-1.5">
                    <MemberAvatar
                      id={attrs.created_by ?? null}
                      name={attrs.creator_name ?? null}
                      email={attrs.creator_email ?? null}
                      size="xs"
                    />
                    <UserName
                      workspaceSlug={workspaceSlug}
                      userId={attrs.created_by ?? null}
                      name={attrs.creator_name ?? null}
                      email={attrs.creator_email ?? null}
                      fallback="—"
                      className="text-foreground truncate"
                    />
                  </span>
                }
              />
              <PanelInlineRow label="Created" value={formatExact(attrs.created_at)} />
              {attrs.updated_at && attrs.updated_at !== attrs.created_at ? (
                <PanelInlineRow
                  label="Edited"
                  value={formatRelativeTime(attrs.updated_at)}
                />
              ) : null}
              {attrs.project_slug ? (
                <PanelInlineRow
                  label="Project"
                  value={
                    <Link
                      to="/w/$workspaceSlug/p/$projectSlug/notes"
                      params={{ workspaceSlug, projectSlug: attrs.project_slug }}
                      className="hover:text-foreground inline-flex items-center gap-1 transition-colors"
                    >
                      <span className="truncate">{attrs.project_name ?? '—'}</span>
                      <ArrowUpRight className="size-3 shrink-0" aria-hidden="true" />
                    </Link>
                  }
                />
              ) : null}
              {attrs.task_id ? (
                <PanelInlineRow
                  label="Task"
                  value={
                    <Link
                      to="/w/$workspaceSlug/t/$taskUlid"
                      params={{ workspaceSlug, taskUlid: attrs.task_id }}
                      className="hover:text-foreground inline-flex items-center gap-1 transition-colors"
                    >
                      <span className="truncate">{attrs.task_title ?? '—'}</span>
                      <ArrowUpRight className="size-3 shrink-0" aria-hidden="true" />
                    </Link>
                  }
                />
              ) : null}
            </PanelCard>
          </aside>
        </div>
      </div>
      {dialog}
    </>
  );
};

/**
 * Placeholder shown while the lazy `NoteEditor` chunk loads. Sized to
 * match the editor's `min-h-[60vh]` so the page doesn't jump when the
 * editor mounts. Static (no shimmer) — the load is sub-200ms on
 * anything but the slowest connections, animating it would draw more
 * eye than the actual delay deserves.
 */
const EditorSkeleton = () => (
  <div className="space-y-2">
    <div
      className="h-10 rounded-md border border-[var(--glass-card-border)] bg-[var(--surface-quiet)]"
      aria-hidden="true"
    />
    <div
      className="text-foreground-faint min-h-[60vh] text-[12.5px] italic"
      role="status"
      aria-live="polite"
    >
      Loading editor…
    </div>
  </div>
);

/**
 * Subtle autosave status pill — sits in the breadcrumb row's right
 * edge. Replaces the explicit Save button so users have a confidence
 * signal that their typing is persisting. Hidden in the idle state to
 * avoid visual noise on first load.
 */
const SaveIndicator = ({ state }: { state: SavingState }) => {
  if (state === 'idle') return null;

  if (state === 'saving') {
    return (
      <span className="text-foreground-faint inline-flex items-center gap-1.5 text-[11px]">
        <span className="bg-muted-foreground/40 size-1.5 animate-pulse rounded-full" />
        Saving…
      </span>
    );
  }

  if (state === 'saved') {
    return (
      <span className="text-foreground-faint inline-flex items-center gap-1.5 text-[11px]">
        <Check className="size-3.5" aria-hidden="true" />
        Saved
      </span>
    );
  }

  return (
    <span className="text-destructive inline-flex items-center gap-1.5 text-xs">
      Failed to save
    </span>
  );
};
